import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Layers, RotateCcw, TrendingUp, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const programs = [
  {
    id: "m4a",
    title: "Meditation for All (M4A)",
    tag: "Spiritual Wellness",
    tagColor: "#E28743",
    objective: "To promote mental well-being and emotional resilience through meditation and mindfulness.",
    audience: "All age groups — youth, working professionals, senior citizens, and rural communities",
    impact: "Reaching 10,000+ individuals through meditation camps and digital wellness programs",
    activities: [
      "Meditation awareness camps",
      "Introductory sessions on Vipassana",
      "Stress management workshops",
      "Digital content for guided practices",
    ],
  },
  {
    id: "sahyog",
    title: "Sahyog Seva Kendras",
    tag: "Social Welfare",
    tagColor: "#606C38",
    objective: "To enable citizens to access government welfare schemes and social security benefits.",
    audience: "Elderly, widows, migrant workers, and families in underserved communities",
    impact: "Establishing 50+ seva kendras across 15 states as holistic community support hubs",
    activities: [
      "Pension application support (old-age, widow, etc.)",
      "Awareness camps on government yojanas",
      "Documentation and verification assistance",
      "Community help centres",
    ],
  },
  {
    id: "swasthya",
    title: "Swasthya Sahayata Abhiyan",
    tag: "Healthcare",
    tagColor: "#023047",
    objective: "To improve access to healthcare and promote preventive well-being.",
    audience: "Rural populations, tribal communities, and urban slum residents",
    impact: "Conducting 100+ health camps annually providing check-ups and basic treatment",
    activities: [
      "Free health check-up camps",
      "Preventive healthcare awareness sessions",
      "Collaborations with doctors and medical professionals",
      "Mobile healthcare initiatives",
    ],
  },
  {
    id: "tech-chetna",
    title: "Tech Chetna",
    tag: "Digital Empowerment",
    tagColor: "#E28743",
    objective: "To empower individuals with essential digital and technical skills.",
    audience: "Rural youth, women, school dropouts, and communities with limited digital exposure",
    impact: "Training 5,000+ individuals annually in digital skills for employability",
    activities: [
      "Basic computer and smartphone training",
      "Digital literacy workshops",
      "Online safety and awareness sessions",
      "Introduction to modern tools and technologies",
    ],
  },
  {
    id: "green-mangalam",
    title: "Green Mangalam",
    tag: "Environment",
    tagColor: "#606C38",
    objective: "To promote environmental sustainability and eco-conscious living.",
    audience: "Schools, colleges, community groups, and organizations committed to sustainability",
    impact: "Planting 50,000+ trees and conducting 200+ environmental awareness workshops",
    activities: [
      "Tree plantation drives",
      "Cleanliness and environmental awareness campaigns",
      "Sustainable lifestyle education",
      "Community participation programs",
    ],
  },
];

const phases = [
  { label: "Phase 1", desc: "Pilot programs at local/community level" },
  { label: "Phase 2", desc: "Expansion through partnerships and collaborations" },
  { label: "Phase 3", desc: "Scaled implementation across regions" },
];

const designPrinciples = [
  { icon: TrendingUp, title: "Scalable", desc: "Can expand across regions" },
  { icon: RotateCcw, title: "Replicable", desc: "Can be implemented in multiple communities" },
  { icon: Layers, title: "Sustainable", desc: "Focused on long-term benefits" },
  { icon: Users, title: "Community-Driven", desc: "Built with local participation" },
];

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, isVisible];
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useInView();
  return (
    <div ref={ref} className={`${className} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function ProgramsPage() {
  return (
    <div data-testid="programs-page" className="pt-20">
      {/* SECTION 1 — INTRODUCTION */}
      <section className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1B201E] mb-6">
              Our Programs & Initiatives
            </h1>
            <p className="text-[#7A827E] text-base sm:text-lg max-w-4xl leading-relaxed">
              At BSM Welfare Foundation, our programs are designed to translate vision into action. Each initiative is structured to address real-world challenges while staying aligned with our philosophy of holistic development — integrating inner awareness with social progress.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* SECTION 2 — IMPLEMENTATION APPROACH */}
      <section data-testid="implementation-approach" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="text-center mb-12">
            <p className="overline mb-3">Execution Model</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-4">Our Implementation Approach</h2>
            <p className="text-[#7A827E] max-w-2xl mx-auto">We follow a phased and scalable execution model to ensure sustainability and measurable impact:</p>
          </AnimatedSection>
          <div className="flex flex-col sm:flex-row items-stretch justify-center gap-6 max-w-3xl mx-auto">
            {phases.map((phase, i) => (
              <AnimatedSection key={i} delay={i * 100} className="flex-1">
                <div className="bg-white rounded-3xl p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-[#E2E5E3]/50 text-center h-full flex flex-col items-center justify-center" data-testid={`phase-${i}`}>
                  <div className="w-10 h-10 rounded-full bg-[#E28743]/10 flex items-center justify-center text-[#E28743] font-['Outfit'] font-bold text-sm mb-3">{i + 1}</div>
                  <p className="font-['Outfit'] font-semibold text-[#1B201E] text-sm mb-1">{phase.label}</p>
                  <p className="text-[#7A827E] text-xs">{phase.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMS LIST */}
      <section data-testid="programs-list" className="py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 space-y-10">
          {programs.map((prog, i) => (
            <AnimatedSection key={prog.id}>
              <Card data-testid={`program-detail-${prog.id}`} className="bg-white border-0 rounded-3xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)] transition-all duration-500 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]">
                <CardContent className="p-8 sm:p-10">
                  <div className="flex items-center gap-4 mb-5">
                    <Badge className="rounded-full px-4 py-1.5 text-xs font-medium border-0" style={{ backgroundColor: `${prog.tagColor}15`, color: prog.tagColor }}>{prog.tag}</Badge>
                  </div>
                  <h3 className="font-['Outfit'] text-2xl font-semibold text-[#1B201E] mb-3">{prog.title}</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div>
                      <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#E28743] mb-2">Objective</p>
                      <p className="text-[#7A827E] text-sm leading-relaxed mb-4">{prog.objective}</p>
                      <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#606C38] mb-1">Target Audience</p>
                      <p className="text-[#7A827E] text-xs leading-relaxed">{prog.audience}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#E28743] mb-2">Key Activities</p>
                      <ul className="space-y-2">
                        {prog.activities.map((act, j) => (
                          <li key={j} className="flex items-start gap-2">
                            <CheckCircle size={14} className="text-[#606C38] mt-0.5 shrink-0" strokeWidth={1.5} />
                            <span className="text-[#7A827E] text-sm">{act}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#023047] mb-2">Impact Goal</p>
                      <div className="bg-[#F3F0E6] rounded-2xl p-4">
                        <p className="text-[#2C3531] text-sm font-medium leading-relaxed">{prog.impact}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* PROGRAM DESIGN PHILOSOPHY */}
      <section data-testid="design-philosophy" className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="text-center mb-12">
            <p className="overline mb-3">Design Principles</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-4">Designed for Sustainable Impact</h2>
            <p className="text-[#7A827E] max-w-2xl mx-auto">Each program under BSM Welfare Foundation is designed to be:</p>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {designPrinciples.map((p, i) => (
              <AnimatedSection key={i} delay={i * 80}>
                <div className="bg-white rounded-3xl p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)] text-center h-full" data-testid={`principle-${i}`}>
                  <div className="w-12 h-12 rounded-2xl bg-[#606C38]/10 flex items-center justify-center mx-auto mb-4">
                    <p.icon size={22} className="text-[#606C38]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-['Outfit'] font-semibold text-[#1B201E] mb-1">{p.title}</h3>
                  <p className="text-[#7A827E] text-sm">{p.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING STATEMENT */}
      <section className="py-20 sm:py-28 bg-[#1B201E]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <AnimatedSection>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-5">From Vision to Action</h2>
            <p className="text-[#7A827E] text-base leading-relaxed max-w-2xl mx-auto mb-5">
              Our programs are not isolated efforts — they are interconnected pathways toward building a society that is aware, empowered, and self-reliant.
            </p>
            <p className="text-[#7A827E] text-base leading-relaxed max-w-2xl mx-auto mb-8">
              With a structured approach and strong intent, BSM Welfare Foundation is committed to transforming ideas into meaningful action.
            </p>
            <Link to="/get-involved" data-testid="programs-cta" className="inline-flex items-center gap-2 bg-[#E28743] text-white hover:bg-[#C96A27] rounded-full px-8 py-3.5 font-medium transition-all duration-300 hover:-translate-y-1 shadow-[0_4px_14px_rgba(226,135,67,0.3)] btn-press text-sm">
              Get Involved <ArrowRight size={16} />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
